import { useEffect, useRef, useState } from 'react';
import { Sparkles, SendHorizonal, Paperclip, X, Wrench, Check, FileSpreadsheet } from 'lucide-react';
import { Card, CardDescription, CardTitle } from '@/components/Card';
import { Button } from '@/components/Button';
import { cn } from '@/lib/cn';
import {
  streamMigrationChat, agentFetch,
  type MigrationSSE, type SetupProfile, type CoaAccount,
  type ImportProposal, type BalancesProposal, type ReadinessCheck,
} from '@/lib/migrationChat';
import { parseSpreadsheet, gridSample, type ParsedFile } from '@/lib/parseSpreadsheet';
import { CoaProposalModal, ImportProposalModal, BalancesProposalModal, ReadinessModal } from './proposals/ProposalModals';

/**
 * MigrationChat — the reimagined, single-surface migration wizard. The whole
 * onboarding (discovery → COA → data import → opening balances → readiness)
 * happens in one chat: the user drops CSV/XLSX files into the box, the model
 * detects the doctype + maps columns, and every confirmation appears as a
 * modal. Nothing mutates the ERP until the user confirms in a popup.
 *
 * Talks to POST /api/agent/v1/migration/{id}/chat/stream (SSE). Full parsed
 * files stay in this component (filesRef); only a small sample is sent.
 */

type Turn =
  | { kind: 'user'; content: string; file?: string }
  | { kind: 'assistant'; content: string; streaming?: boolean }
  | { kind: 'tool'; name: string; ok?: boolean }
  | { kind: 'event'; content: string };

type Modal =
  | { type: 'coa'; accounts: CoaAccount[] }
  | { type: 'import'; proposal: ImportProposal }
  | { type: 'balances'; proposal: BalancesProposal }
  | { type: 'readiness'; items: ReadinessCheck[] };

const GREETING =
  'Halo! Saya akan memandu setup Logica ERP dari awal sampai siap go-live — semua lewat chat ini. Ceritakan dulu bisnis Anda bergerak di bidang apa. Nanti untuk migrasi data, cukup tarik & lepas file CSV/Excel ke kotak chat di bawah.';

export function MigrationChat({ sessionId, onActivity }: { sessionId: string; onActivity?: () => void }) {
  const [turns, setTurns] = useState<Turn[]>([{ kind: 'assistant', content: GREETING }]);
  const [draft, setDraft] = useState('');
  const [pending, setPending] = useState<ParsedFile | null>(null);
  const [parseErr, setParseErr] = useState<string | null>(null);
  const [streaming, setStreaming] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [profile, setProfile] = useState<SetupProfile | undefined>();
  const [modal, setModal] = useState<Modal | null>(null);

  const filesRef = useRef<Map<string, ParsedFile>>(new Map());
  const scrollRef = useRef<HTMLDivElement>(null);

  // Resume: load prior messages so a refresh doesn't lose the conversation.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await agentFetch<{ items: Array<{ role: string; content: string }> }>(`/api/agent/v1/sessions/${sessionId}/messages`);
        if (cancelled || !r.items?.length) return;
        const prior: Turn[] = [];
        for (const m of r.items) {
          if (m.role === 'user') prior.push({ kind: 'user', content: stripAttachment(m.content) });
          else if (m.role === 'assistant' && m.content.trim()) prior.push({ kind: 'assistant', content: m.content });
        }
        if (prior.length) setTurns([{ kind: 'assistant', content: GREETING }, ...prior]);
      } catch { /* fresh session — keep greeting */ }
    })();
    return () => { cancelled = true; };
  }, [sessionId]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' });
  }, [turns, streaming]);

  function pushTurn(t: Turn) { setTurns((ts) => [...ts, t]); }

  function appendDelta(chunk: string) {
    setTurns((ts) => {
      const last = ts[ts.length - 1];
      if (last && last.kind === 'assistant' && last.streaming) {
        return [...ts.slice(0, -1), { ...last, content: last.content + chunk }];
      }
      return [...ts, { kind: 'assistant', content: chunk, streaming: true }];
    });
  }

  function finishStreaming() {
    setTurns((ts) => ts.map((t, i) => (i === ts.length - 1 && t.kind === 'assistant' ? { ...t, streaming: false } : t)));
  }

  async function runStream(message: string, file: ParsedFile | null, opts: { silentUser?: boolean } = {}) {
    setStreaming(true);
    let attachment;
    if (file) {
      const id = `att_${Date.now()}_${filesRef.current.size}`;
      filesRef.current.set(id, file);
      attachment = { id, filename: file.filename, grid_sample: gridSample(file.grid), total_rows: file.rowCount };
    }
    if (!opts.silentUser) {
      pushTurn({ kind: 'user', content: message, file: file?.filename });
    } else if (message) {
      pushTurn({ kind: 'event', content: message });
    }
    try {
      await streamMigrationChat(sessionId, { message, attachment }, (ev: MigrationSSE) => onEvent(ev));
    } catch (e) {
      pushTurn({ kind: 'event', content: `Error: ${(e as Error).message}` });
    } finally {
      finishStreaming();
      setStreaming(false);
    }
  }

  function onEvent(ev: MigrationSSE) {
    switch (ev.kind) {
      case 'delta': appendDelta(ev.data.content); break;
      case 'tool_call': pushTurn({ kind: 'tool', name: ev.data.name }); break;
      case 'tool_result':
        setTurns((ts) => {
          const i = [...ts].reverse().findIndex((t) => t.kind === 'tool' && t.name === ev.data.name && t.ok === undefined);
          if (i === -1) return ts;
          const idx = ts.length - 1 - i;
          return ts.map((t, j) => (j === idx && t.kind === 'tool' ? { ...t, ok: ev.data.ok } : t));
        });
        break;
      case 'profile':
        setProfile(ev.data.profile);
        onActivity?.();
        break;
      case 'coa_proposal': setModal({ type: 'coa', accounts: ev.data.accounts }); break;
      case 'import_proposal': setModal({ type: 'import', proposal: ev.data }); break;
      case 'balances_proposal': setModal({ type: 'balances', proposal: ev.data }); break;
      case 'readiness': setModal({ type: 'readiness', items: ev.data.items }); break;
      case 'error': pushTurn({ kind: 'event', content: `Error: ${ev.data.message}` }); break;
      case 'done': onActivity?.(); break;
      default: break;
    }
  }

  function submit() {
    if (streaming) return;
    const msg = draft.trim();
    if (!msg && !pending) return;
    const file = pending;
    setDraft('');
    setPending(null);
    void runStream(msg, file);
  }

  // Result of a confirmed modal → feed back into the chat so the model continues.
  function onModalResult(text: string) {
    setModal(null);
    void runStream(text, null, { silentUser: true });
  }

  async function handleFiles(fileList: FileList | null) {
    setParseErr(null);
    const f = fileList?.[0];
    if (!f) return;
    try {
      setPending(await parseSpreadsheet(f));
    } catch (e) {
      setParseErr((e as Error).message);
    }
  }

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[1fr,260px] gap-4 items-start">
      <Card
        padded={false}
        className={cn('flex flex-col h-[calc(100dvh-260px)] min-h-[420px] lg:h-[calc(100vh-220px)] lg:min-h-[460px] relative', dragOver && 'ring-2 ring-accent')}
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => { e.preventDefault(); setDragOver(false); void handleFiles(e.dataTransfer.files); }}
      >
        {dragOver && (
          <div className="absolute inset-0 z-10 bg-accent/5 border-2 border-dashed border-accent rounded-lg flex items-center justify-center pointer-events-none">
            <div className="text-accent text-body-sm font-medium"><FileSpreadsheet className="size-5 inline mr-1.5" />Lepas file CSV/Excel di sini</div>
          </div>
        )}

        <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3">
          {turns.map((t, i) => <TurnView key={i} turn={t} />)}
          {streaming && turns[turns.length - 1]?.kind !== 'assistant' && <ThinkingDots />}
        </div>

        <div className="border-t border-hairline p-3">
          {pending && (
            <div className="mb-2 inline-flex items-center gap-2 text-caption bg-surface-soft border border-hairline rounded-md px-2 py-1">
              <FileSpreadsheet className="size-3.5 text-accent" />
              <span className="text-charcoal">{pending.filename}</span>
              <span className="text-stone">· {pending.rowCount} rows</span>
              <button type="button" onClick={() => setPending(null)} className="text-stone hover:text-ink"><X className="size-3.5" /></button>
            </div>
          )}
          {parseErr && <div className="mb-2 text-caption text-brand-error">{parseErr}</div>}
          <div className="flex items-end gap-2">
            <label className="cursor-pointer text-stone hover:text-accent p-2" title="Lampirkan CSV/Excel">
              <Paperclip className="size-4" />
              <input type="file" accept=".csv,.tsv,.txt,.xlsx,.xls" className="sr-only"
                onChange={(e) => { void handleFiles(e.target.files); e.target.value = ''; }} />
            </label>
            <textarea
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); submit(); } }}
              rows={1}
              placeholder="Tulis pesan, atau tarik file ke sini…"
              className="flex-1 resize-none px-3 py-2 rounded-md border border-hairline bg-surface text-body-sm focus:outline-none focus:ring-2 focus:ring-accent max-h-32"
            />
            <Button onClick={submit} loading={streaming} disabled={!draft.trim() && !pending}>
              <SendHorizonal className="size-4" />
            </Button>
          </div>
        </div>
      </Card>

      <ProfilePanel profile={profile} />

      {modal?.type === 'coa' && (
        <CoaProposalModal accounts={modal.accounts} sessionId={sessionId} onResult={onModalResult} onClose={() => setModal(null)} />
      )}
      {modal?.type === 'import' && (
        <ImportProposalModal proposal={modal.proposal} file={filesRef.current.get(modal.proposal.attachment_id)} sessionId={sessionId} onResult={onModalResult} onClose={() => setModal(null)} />
      )}
      {modal?.type === 'balances' && (
        <BalancesProposalModal proposal={modal.proposal} file={filesRef.current.get(modal.proposal.attachment_id)} sessionId={sessionId} onResult={onModalResult} onClose={() => setModal(null)} />
      )}
      {modal?.type === 'readiness' && (
        <ReadinessModal items={modal.items} onClose={() => setModal(null)} />
      )}
    </div>
  );
}

function TurnView({ turn }: { turn: Turn }) {
  if (turn.kind === 'tool') {
    return (
      <div className="flex justify-start">
        <div className="inline-flex items-center gap-1.5 text-caption text-stone bg-surface-soft border border-hairline rounded-full px-2.5 py-1">
          {turn.ok === undefined ? <Wrench className="size-3 animate-pulse" /> : turn.ok ? <Check className="size-3 text-brand-success" /> : <X className="size-3 text-brand-error" />}
          {prettyTool(turn.name)}
        </div>
      </div>
    );
  }
  if (turn.kind === 'event') {
    return <div className="flex justify-center"><div className="text-caption text-stone italic max-w-[90%] text-center">{turn.content}</div></div>;
  }
  const isUser = turn.kind === 'user';
  return (
    <div className={cn('flex', isUser ? 'justify-end' : 'justify-start')}>
      <div className={cn('max-w-[85%] rounded-lg px-3 py-2 text-body-sm whitespace-pre-wrap', isUser ? 'bg-accent text-white' : 'bg-surface-soft text-charcoal border border-hairline')}>
        {turn.kind === 'user' && turn.file && (
          <div className={cn('inline-flex items-center gap-1 text-caption mb-1', 'opacity-90')}><FileSpreadsheet className="size-3" />{turn.file}</div>
        )}
        {turn.content || (turn.kind === 'assistant' && turn.streaming ? '…' : '')}
      </div>
    </div>
  );
}

function ThinkingDots() {
  return (
    <div className="flex justify-start">
      <div className="rounded-lg px-3 py-2 bg-surface-soft border border-hairline text-stone">
        <span className="inline-flex gap-1">
          <span className="size-1.5 rounded-full bg-stone animate-pulse" />
          <span className="size-1.5 rounded-full bg-stone animate-pulse [animation-delay:150ms]" />
          <span className="size-1.5 rounded-full bg-stone animate-pulse [animation-delay:300ms]" />
        </span>
      </div>
    </div>
  );
}

function ProfilePanel({ profile }: { profile: SetupProfile | undefined }) {
  const rows: Array<[string, string]> = [
    ['Tipe bisnis', profile?.business_type || ''],
    ['Industri', profile?.industry || ''],
    ['Karyawan', profile?.employees ? String(profile.employees) : ''],
    ['Modul', profile?.modules?.length ? profile.modules.join(', ') : ''],
    ['Multi-company', profile ? (profile.multicompany ? 'Ya' : 'Tidak') : ''],
    ['Awal tahun fiskal', profile?.fiscal_year_start || ''],
    ['Mata uang', profile?.base_currency || ''],
    ['Sistem lama', profile?.legacy_system || ''],
  ];
  return (
    <Card className="lg:sticky lg:top-6">
      <CardTitle><Sparkles className="size-3.5 inline mr-1 text-accent" />Profil terkumpul</CardTitle>
      <CardDescription>Diisi otomatis selama percakapan.</CardDescription>
      <dl className="mt-3 space-y-2">
        {rows.map(([k, v]) => (
          <div key={k} className="flex flex-col">
            <dt className="text-caption text-stone">{k}</dt>
            <dd className={cn('text-body-sm', v ? 'text-ink' : 'text-stone/50 italic')}>{v || 'belum diisi'}</dd>
          </div>
        ))}
      </dl>
    </Card>
  );
}

function prettyTool(name: string): string {
  switch (name) {
    case 'save_setup_profile': return 'Mencatat profil';
    case 'propose_coa': return 'Menyiapkan chart of accounts';
    case 'propose_data_import': return 'Memetakan kolom data';
    case 'propose_opening_balances': return 'Membaca neraca saldo';
    case 'run_readiness': return 'Mengecek kesiapan';
    default: return name;
  }
}

function stripAttachment(content: string): string {
  const i = content.indexOf('\n\n[Attached file:');
  return i === -1 ? content : content.slice(0, i);
}
